/**
 * Functions specific to making API requests using Axios, e.g. Facade pattern,
 * interceptors etc.
 */

import axios from "axios";

import { API_ENDPOINTS, REQUEST_CONFIG } from "../../config";

// Set up default axios configuration
axios.defaults.withCredentials = REQUEST_CONFIG.withCredentials;
axios.defaults.xsrfCookieName = REQUEST_CONFIG.xsrfCookieName;
axios.defaults.xsrfHeaderName = REQUEST_CONFIG.xsrfHeaderName;
axios.defaults.timeout = REQUEST_CONFIG.timeout;

/**
 * Axios facade pattern to fetch data from the API.
 *
 * @param {String} url The API url to fetch data from
 * @param {Function} setData The state setter function to store the API response data
 * @returns API response data (see API documentation for details)
 */
export async function getAPIData(url, setData) {
  return await apiRequest(url, null, { method: "GET" }, setData);
}

/**
 * POST helper that reuses `apiRequest` and optionally writes into a state setter.
 *
 * @param {string} url Path relative to the API base.
 * @param {Function} [setData] Optional state setter for the response body.
 * @param {Object} [body] JSON or FormData payload.
 * @param {Object} [config] Extra axios config merged into the POST.
 * @returns {Promise<*>} API response data.
 */
export async function postAPIData(url, setData, body, config) {
  return await apiRequest(url, body, { method: "POST", ...config }, setData);
}

/**
 * Session-authenticated request to the Django `/api` base.
 *
 * CSRF is read from the `csrftoken` cookie because `CSRF_COOKIE_HTTPONLY` is
 * False so React can send `X-CSRFTOKEN` on state-changing calls.
 *
 * @param {string} url Path relative to `API_ENDPOINTS.BASE`.
 * @param {Object|FormData|null} body Request payload.
 * @param {Object} [config] Axios config (`method`, headers, timeout, signal).
 * @param {Function} [callback] Optional side-effect setter for the response.
 * @returns {Promise<*>} Parsed response body (or the axios response if no `.data`).
 */
export async function apiRequest(url, body, config, callback) {
  const apiUrl = API_ENDPOINTS.BASE;

  // Cookie is readable on purpose (not HttpOnly) so the SPA can send CSRF.
  const csrfToken = document.cookie
    .split("; ")
    .find((row) => row.startsWith("csrftoken"))
    ?.split("=")[1];

  const safeConfig = config || {};
  const { headers: requestHeaders, ...axiosRest } = safeConfig;

  const response = await axios({
    url: `${apiUrl}${url}`,
    ...REQUEST_CONFIG,
    ...axiosRest,
    data: body || undefined,
    headers: {
      "X-CSRFTOKEN": csrfToken || "",
      ...(requestHeaders || {}),
    },
  });
  const responseData = response?.data || response;
  if (callback) {
    callback(responseData);
  }
  return responseData;
}

/**
 * Request against the Egnyte API base (no Django CSRF header).
 *
 * @param {string} url Path relative to `API_ENDPOINTS.EGNYTE`.
 * @param {Object|FormData|null} body Request payload.
 * @param {Object} [config] Axios config merged last.
 * @param {Function} [callback] Optional side-effect setter for the response.
 * @returns {Promise<*>} Parsed response body (or the axios response if no `.data`).
 */
export async function egnyteApiRequest(url, body, config, callback) {
  const apiUrl = API_ENDPOINTS.EGNYTE;

  const response = await axios({
    url: `${apiUrl}${url}`,
    ...REQUEST_CONFIG,
    data: body || undefined,
    headers: {},
    ...(config || {}),
  });
  const responseData = response?.data || response;
  if (callback) {
    callback(responseData);
  }
  return responseData;
}
